import {CompositionCompetence} from '@letrustech/letrus-api-interfaces/dist/interfaces/letrus_api';
import {WritePreparation} from 'APIs/letrusAPI/writePreparation/types';
import bookShelf from 'assets/images/multigenre/bookshelf.webp';
import circleCheck from 'assets/images/multigenre/circle-check.webp';
import glasses from 'assets/images/multigenre/glasses.webp';
import magnifyingGlass from 'assets/images/multigenre/magnifying-glass.webp';
import puzzlePiece from 'assets/images/multigenre/puzzle-piece.webp';
import textBaloon from 'assets/images/multigenre/text-baloon.webp';
import {RadioChips} from 'containers/CompositionFeedback/views/GradesView/RadioChipList';
import {useState} from 'react';
import {UserFeelingsStateData} from 'store/reducers/userFeelings';
import {CompositionDetailExtended} from 'utils/types/compositions';

export interface UseCompositionFeedbackParams {
  composition: ImmutableMap<CompositionDetailExtended>;
  writePreparation?: WritePreparation;
  userFeelings?: UserFeelingsStateData;
}

interface useCompositionFeedbackReturn {
  instructions: string;
  generalScore: {
    max: number;
    student: number;
  };
  conversationList: {
    generalReview: string[];
    inProgressReviewMessage: string;
    emotionMessage: string[];
    currentGradeMessageList: string[];
  };
  gradeList: RadioChips[];
}

function useCompositionFeedback({
  composition,
  writePreparation,
  userFeelings,
}: UseCompositionFeedbackParams): useCompositionFeedbackReturn {
  const [currentCompetence, setCurrentCompetence] =
    useState<CompositionCompetence | undefined>();
  const {reviews, genre, user, aids, instruction_id} = composition?.toJS();

  const CompetencesImages = {
    image0: puzzlePiece,
    image1: bookShelf,
    image2: glasses,
    image3: magnifyingGlass,
    image4: textBaloon,
    image5: circleCheck,
  };

  function getCurrentMessageList(): string[] {
    const defaultMessageList = [
      `${user?.first_name}, logo abaixo estão alguns comentários valiosos sobre o seu texto.`,
      'Clique em cada um dos critérios, faça uma leitura atenciosa e aproveite para colher estratégias para uma próxima produção!',
    ];

    const currentCompetenceGrade = reviews?.[0]?.review_grades?.find(
      (grade) => grade.competence === currentCompetence?.id,
    );

    if (!currentCompetenceGrade) {
      return defaultMessageList;
    }

    const currentCompetenceComment = currentCompetenceGrade?.comments
      ?.split('\n' || '</p>')
      .reduce<string[]>(
        (coversation, paragraph) =>
          paragraph.length
            ? [...coversation, paragraph.replace(/(<.*?>)|(\n)/g, '')]
            : coversation,
        [],
      );
    const currentMessageList = [
      `Vamos falar sobre “${currentCompetence?.name}”, critério que ${currentCompetence?.description_student}.`,
      `Sua nota nesse critério foi ${currentCompetenceGrade.grade_item.points} de ${currentCompetence?.max_value_range}`,
      ...(currentCompetenceComment ?? []),
    ];

    return currentMessageList;
  }

  const aidsHtml = aids
    ?.map(
      (aid) => `<h3>${aid.title}</h3>${
        aid.content_html ? `<p>${aid.content_html}</p>` : ''
      } 
    ${
      aid.content_image
        ? `<figure>
        <img src='${aid.content_image}'/>
      </figure>`
        : ''
    } 
    `,
    )
    .join('');

  const instructions = `${instruction_id?.content_html} ${
    writePreparation?.aids_markup_html ?? aidsHtml
  }`;

  const inProgressReviewMessage = `${user?.first_name}, o seu texto ainda está sendo analisado. Ao continuar, você pode reler o que escreveu, mas sem acesso à devolutiva, então lembre-se de voltar mais tarde, certo?`;

  const hasNotSkippedEmotion = !!(
    (userFeelings?.choice || userFeelings?.comment) &&
    userFeelings?.choice.value !== 'skipped'
  );

  const emotionMessage = [
    hasNotSkippedEmotion
      ? `${user?.first_name}, antes de explorarmos sua devolutiva, você me contou que estava ${userFeelings?.choice.display}. E agora, como você está se sentindo?`
      : `${user?.first_name}, antes de explorarmos sua devolutiva, você preferiu não me contar como estava se sentindo. E agora, você gostaria de compartilhar? Basta clicar em uma das carinhas.`,
    ` Você pode responder quantas vezes desejar!`,
  ];

  const generalReviewConversation = reviews?.[0]?.general_comments
    ?.split('\n' || '</p>')
    .reduce<string[]>(
      (coversation, paragraph) =>
        paragraph.length
          ? [...coversation, paragraph.replace(/(<.*?>)|(\n)/g, '')]
          : coversation,
      [],
    );

  const gradeList: RadioChips[] =
    genre?.competences?.map((competence, index) => {
      return {
        name: competence.name,
        imageUrl: CompetencesImages[`image${index}`],
        id: competence.id,
        onClick: () => {
          setCurrentCompetence(competence);
        },
      };
    }) ?? [];

  const generalStudentScore = Math.round(
    reviews?.[0]?.review_grades?.reduce(
      (total, grade) => total + (grade?.grade_item?.points ?? 0),
      0,
    ) / reviews?.[0]?.review_grades?.length,
  );

  const generalMaxScore = Math.round(
    genre?.competences?.reduce(
      (total, competence) => total + competence.max_value_range,
      0,
    ) / genre?.competences?.length,
  );

  return {
    generalScore: {max: generalMaxScore, student: generalStudentScore},
    conversationList: {
      generalReview: generalReviewConversation,
      inProgressReviewMessage,
      currentGradeMessageList: getCurrentMessageList(),
      emotionMessage,
    },
    instructions,
    gradeList,
  };
}

export default useCompositionFeedback;
